import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PublicLayout from '@/components/hydlib/PublicLayout';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [showForgot, setShowForgot] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetMessage, setResetMessage] = useState('');
    const [resetError, setResetError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        const result = await login(email, password, rememberMe);
        if (!result.success) setError(result.error || 'Login failed');
        setLoading(false);
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        setResetLoading(true);
        setResetError('');
        setResetMessage('');
        try {
            const res = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
            setResetMessage(res.data.message || "If an account exists, we've sent a reset link.");
            setResetEmail('');
        } catch (err) {
            setResetError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <PublicLayout theme="archive" grid="--a1" gridStrength={30}>
            <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a2) 0 5px, transparent 6px)' }} />
            <div className="hydlib-cols hydlib-pad" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'start', maxWidth: 1080, margin: '0 auto', padding: '72px 48px' }}>
                {/* LEFT — Log in */}
                <div style={{ position: 'relative', border: '1px solid var(--line)', background: 'var(--paper)', padding: '40px 36px' }}>
                    <span style={{ position: 'absolute', top: -16, left: 28, fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600, color: 'var(--a1)', transform: 'rotate(-3deg)', background: 'var(--bg)', padding: '0 8px' }}>welcome back, bookworm</span>
                    <h1 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 36, margin: '0 0 24px' }}>Log in</h1>

                    {!showForgot ? (
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                                Email
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    data-testid="login-email"
                                    style={{ border: '1px solid var(--line)', background: 'var(--bg)', padding: '12px 14px', fontSize: 16, color: 'var(--ink)', fontFamily: 'inherit' }}
                                />
                            </label>
                            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                                Password
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                    data-testid="login-password"
                                    style={{ border: '1px solid var(--line)', background: 'var(--bg)', padding: '12px 14px', fontSize: 16, color: 'var(--ink)', fontFamily: 'inherit' }}
                                />
                            </label>

                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} data-testid="login-remember" />
                                Keep me logged in until I log out
                            </label>

                            {error && <div style={{ color: 'var(--a2)', fontSize: 14, padding: '6px 10px', border: '1px solid var(--a2)', background: 'color-mix(in oklch, var(--a2) 10%, transparent)' }} data-testid="login-error">{error}</div>}

                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="login-submit"
                                className="hydlib-highlight"
                                style={{
                                    cursor: loading ? 'wait' : 'pointer',
                                    border: 'none',
                                    color: 'var(--ink)',
                                    fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 15,
                                    letterSpacing: 1.5, textTransform: 'uppercase', padding: 14, marginTop: 8,
                                    background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)`,
                                    opacity: loading ? 0.7 : 1,
                                }}
                            >
                                {loading ? 'Logging in…' : 'Log in → My Library'}
                            </button>

                            <button type="button" onClick={() => setShowForgot(true)} data-testid="login-forgot" style={{ fontSize: 14, textAlign: 'center', color: 'var(--a3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                Forgot password?
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <p style={{ fontSize: 14, margin: 0 }}>Enter your email — we&apos;ll send you a reset link.</p>
                            <input
                                type="email"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                data-testid="forgot-email"
                                style={{ border: '1px solid var(--line)', background: 'var(--bg)', padding: '12px 14px', fontSize: 16, color: 'var(--ink)', fontFamily: 'inherit' }}
                            />
                            {resetMessage && <div style={{ color: 'var(--a3)', fontSize: 14 }} data-testid="forgot-success">{resetMessage}</div>}
                            {resetError && <div style={{ color: 'var(--a2)', fontSize: 14 }} data-testid="forgot-error">{resetError}</div>}
                            <button type="submit" disabled={resetLoading} data-testid="forgot-submit" style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--a3)', color: 'var(--o3)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 14, letterSpacing: 1.5, textTransform: 'uppercase', padding: 12 }}>
                                {resetLoading ? 'Sending…' : 'Send reset link'}
                            </button>
                            <button type="button" onClick={() => { setShowForgot(false); setResetError(''); setResetMessage(''); }} style={{ fontSize: 14, textAlign: 'center', color: 'var(--a3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                                ← Back to login
                            </button>
                        </form>
                    )}
                </div>

                {/* RIGHT — New here? */}
                <div style={{ position: 'relative', border: '1px solid var(--line)', background: 'var(--a1)', color: 'var(--o1)', padding: '40px 36px', transform: 'rotate(0.5deg)' }}>
                    <h2 style={{ fontFamily: "'Libre Caslon Display', serif", fontWeight: 400, fontSize: 36, margin: '0 0 12px' }}>New here?</h2>
                    <p style={{ fontSize: 16, lineHeight: 1.65, margin: '0 0 24px' }}>Unlimited books, five at a time, one open door a week. Pick a plan and you&apos;re reading by the weekend.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 28, fontFamily: "'Space Mono', monospace", fontSize: 13, letterSpacing: 1 }}>
                        <div style={{ border: '1px solid var(--o1)', padding: '14px 16px', display: 'flex', justifyContent: 'space-between' }}>
                            <span>MONTHLY</span><span>₹500</span>
                        </div>
                        <div style={{ position: 'relative', border: '1px solid var(--o1)', background: 'color-mix(in oklch, var(--o1) 16%, transparent)', padding: '14px 16px', display: 'flex', justifyContent: 'space-between' }}>
                            <span>YEARLY</span><span>₹5,000</span>
                            <span style={{ position: 'absolute', top: -12, right: 10, background: 'var(--a2)', color: 'var(--o2)', fontSize: 11, padding: '3px 8px', border: '1px solid var(--line)' }}>2 MONTHS FREE</span>
                        </div>
                        <div style={{ border: '1px solid var(--o1)', padding: '14px 16px', display: 'flex', justifyContent: 'space-between' }} data-testid="login-kids-plan">
                            <span>KIDS (BOOKS FOR CHILDREN)</span><span>₹300/mo</span>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/signup')}
                        data-testid="signup-cta"
                        style={{ cursor: 'pointer', display: 'block', textAlign: 'center', width: '100%', background: 'var(--paper)', color: 'var(--ink)', fontFamily: "'Space Mono', monospace", fontWeight: 700, fontSize: 15, letterSpacing: 1.5, textTransform: 'uppercase', padding: 14, border: '1px solid var(--line)' }}
                    >
                        Sign up
                    </button>
                    <span style={{ position: 'absolute', bottom: -18, right: 30, fontFamily: "'Caveat', cursive", fontSize: 21, fontWeight: 600, color: 'var(--a3)', transform: 'rotate(-3deg)' }}>takes 2 minutes, promise</span>
                </div>
            </div>

            {/* Decorative book-spine pill stack */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '0 auto 60px', width: 'fit-content' }}>
                <div className="hydlib-doodle" style={{ width: 190, height: 26, background: 'var(--a3)', color: 'var(--o3)', transform: 'rotate(-1.5deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2 }}>FICTION</div>
                <div className="hydlib-doodle" style={{ width: 220, height: 30, background: 'var(--a1)', color: 'var(--o1)', transform: 'rotate(1deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2 }}>POETRY!</div>
                <div className="hydlib-doodle" style={{ width: 175, height: 24, background: 'var(--a2)', color: 'var(--o2)', transform: 'rotate(-2deg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2 }}>COMICS</div>
                <div style={{ width: 240, borderBottom: '2px solid var(--line)', marginTop: 4 }} />
                <div style={{ fontFamily: "'Caveat', cursive", fontSize: 20, fontWeight: 600, color: 'var(--a1)', marginTop: 10, transform: 'rotate(-2deg)' }}>your next five await</div>
            </div>
        </PublicLayout>
    );
}
